import { authFetchGET, authFetchPOST, FetchStatusCodeError } from '../utils/auth-fetch';
import { ForecastInfo, SpotWeatherInfo } from '../models/weather-model';

const env = (<any>window)["appenv"].services;

// Mapping between the Spot APIs numbered weather elements and the text names used elsewhere
const elementsInCodeOrder = [
    "Pressure",
    "HPa500",
    "Wind10M",
    "Wind50M",
    "WindGusts10M",
    "WindGusts50M",
    "RiskWindSpeed",
    "RiskWaveHeight",
    "Wave",
    "WavePeriod",
    "Swell",
    "SwellPeriod",
    "Precipitation",
    "Visibility",
    "RelativeHumidity",
    "AirTemperature",
    "DewPoint",
    "Icing",
    "IceConcentration",
    "FreezingLevel",
    "IceBergPosition",
    "Currents",
    "SeaWaterTemperature",
    "SeaWaterSalinity",
    "WeatherFrontData",
    "HighLowPressureSymbols",
    "WeatherCode",
    "Hurricanes",
    "Wind30M",
    "SeaWaterDensity"
];

// Forecast information as received from the DTN API
export interface ForecastMetadata
{
    name: string;
    timestamp: string;
    forecast_from: string;
    forecast_to: string;

    // only present for vessel version
    subscriptions?: {
        id: string;
        import_date: string;
    }[];
}

export const WeatherService = new class extends EventTarget
{
    weatherForecastMonitorDisabled: boolean = !env.weatherforecastmonitor || !env.weatherforecastmonitor.baseurl;

    async vesselGetLatestAvailableForecast(): Promise<null|ForecastInfo>
    {
        if (this.weatherForecastMonitorDisabled)
        {
            // voy-vessel-weatherforecastmonitor usage disabled
            return null;
        }

        let latestForecast: null|ForecastInfo = null;
        try
        {
            latestForecast = await authFetchGET(env.weatherforecastmonitor.scope, `${env.weatherforecastmonitor.baseurl}/v1/forecasts/latest`);
        }
        catch (err)
        {
            console.warn("Failed to get latest available forecast:", err);
        }
        return latestForecast;
    }

    // Return the forecasts within a time range
    async getForecasts(timeFrom?: Date, timeTo?: Date)
    {
        const filter: { timestamp_from?:string, timestamp_to?: string } = {};
        if (timeFrom)
        {
            filter.timestamp_from = timeFrom.toISOString();
        }
        if (timeTo)
        {
            filter.timestamp_to = timeTo.toISOString();
        }

        const forecasts: ForecastMetadata[] = await authFetchGET(env.weather.scope, `${env.weather.baseurl}/api/v1/forecasts`, <{[index:string]: string}>filter);

        if (!forecasts) {
            console.warn('Forecasts could not be retrieved.');
            return null;
        }

        // Remove special forecasts like tide (prefixed with 'T') and cyclones (prefixed with 'C')
        return forecasts.filter( forecast => forecast.name.startsWith("F") );
    }

    async getSpotWeather(coordinates: [number, number], elementNames: string[], timestamp: string, forecastVersion: string)
    {    
        let payload = {locations: [ { coordinates, timestamp } ], elements: elementNames.map( e => this.weatherElementToCode(e) ), climatic_fallback: true};
        const spotWeather: SpotWeatherInfo = await authFetchPOST(env.weather.scope, `${env.weather.baseurl}/api/v1/${forecastVersion}/spot`, payload);
       
        if (!spotWeather)
        {
            throw new Error(`Failed to fetch spot weather for ${coordinates} at ${timestamp}`)
        }
        //@ts-ignore
        this._fixupSpotWeather(spotWeather.locations[0], timestamp);

        return spotWeather;
    }

    async getMultiSpotWeather(locations: { coordinates: [number, number], timestamp: string }[], 
                              elements: string[], forecastVersion: string)
    {
        let weatherLocations: SpotWeatherInfo[] = [];

        const processBatch = async (locationsBatch: { coordinates: [number, number], timestamp: string }[]): Promise<void> => {
            try
            {
                const result = await authFetchPOST(env.weather.scope, `${env.weather.baseurl}/api/v1/${forecastVersion}/spot`, {
                    locations: locationsBatch,
                    elements: elements.map( e => this.weatherElementToCode(e) ),
                    climatic_fallback: true
                });    

                weatherLocations = weatherLocations.concat(result.locations);
                weatherLocations = weatherLocations.concat(result.missing_in_forecast);
            }
            catch (err)
            {
                if (err instanceof FetchStatusCodeError) {
                    // Cloud version of spot weather often returns 429 (too many requests), retry when this happens
                    if (err.response.status === 429) {
                        return processBatch(locationsBatch);
                    }
                }

                // If not handled, rethrow error
                throw err;
            }
        }

        if (locations.length > 200) {
            // Need to carve up the request so we don't exceed 200 per fetch
            let fetches: Promise<void>[] = [];
            let index = 0;
            while (index < locations.length)
            {
                fetches.push(processBatch(locations.slice(index, index+200)));
                index += 200;
            }
            await Promise.all(fetches);
        }
        else {
            await processBatch(locations);
        }

        if (weatherLocations.length === 0) {
            console.warn('Failed to fetch multi spot weather for', locations);
            return [];
        }

        for (let i = 0; i < weatherLocations.length; i++)
        {
            // For multi-spot, the API returns the correct timestamp, so we don't need to pass the second parameter
            this._fixupSpotWeather(weatherLocations[i]);
        }

        return weatherLocations;
    }

    async getWeatherElements()
    {
        return await authFetchGET(env.weather.scope, `${env.weather.baseurl}/api/v1/elements`);
    }

    weatherCodeToElement(number: number)
    {
        return elementsInCodeOrder[number];
    }

    weatherElementToCode(elementName: string)
    {
        return elementsInCodeOrder.indexOf(elementName);
    }

    private _fixupSpotWeather(spotWeather: SpotWeatherInfo, inputTimestamp?: string): void
    {
        // Fixups:
        // - Convert back from element numbers to names
        // - Remove the first element in the value array (which is the number of values, aka length of the array... sigh)
        // - Remove elements where the values are "NaN" instead of a valid number (sigh)
        // - Correct the timestamp - it should be the time we asked for, but they return the weather data date instead
        //   (completely off for climatic weather) - this is no longer the case with R11
        for (let i = spotWeather.properties.weather.length-1; i >= 0; i--)
        {
            const weather = spotWeather.properties.weather[i];
            if (isNaN(Number(weather.value[1])))
            {
                // Remove this weather element since we don't have a valid value anyway
                spotWeather.properties.weather.splice(i, 1);
            }
            else
            {
                weather.element = this.weatherCodeToElement(weather.element);
                weather.value = weather.value.slice(1);
            }
        }
        if (inputTimestamp)
        {
            spotWeather.properties.timestamp = inputTimestamp;
        }
    }
};